import { createContext, FC, useEffect, useState } from 'react';
import { SpaceApi, loadSpace, InitOptions } from '@usersnap/browser';

import { getUserSnapRoleFromUserRolesArray } from 'helpers/roles/roles.helper';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useLazyGetSystemSettingsQuery } from 'services/teamHeroApi.service';

const UsersnapContext = createContext<SpaceApi | null>(null);

export const UsersnapProvider: FC = () => {
  const [usersnapApi, setUsersnapApi] = useState<SpaceApi | null>(null);
  const usersnapGlobalApiKey = import.meta.env.VITE_USERSNAP_GLOBAL_API_KEY;

  const user = useCurrentUser();
  const [fetchSystemSettings] = useLazyGetSystemSettingsQuery();

  useEffect(() => {
    // only load usersnap in production and if the global api key is set
    if (import.meta.env.PROD && usersnapGlobalApiKey) {
      loadSpace(usersnapGlobalApiKey).then(async (api: SpaceApi) => {
        // if the user is not logged in, we need to destroy the usersnap session
        // and reinitialize it with the anonymous user role
        await api.destroy();
        const userObject: InitOptions['user'] = user.email
          ? {
              userId: user.email,
              email: user.email,
              role: getUserSnapRoleFromUserRolesArray(user.roles),
            }
          : {
              role: 'ANONYMOUS',
            };

        await api.init({
          collectGeoLocation: 'none',
          useLocalStorage: true,
          useSystemFonts: true,
          nativeScreenshot: true,
          user: userObject,
          custom: {
            environment: 'HEROLOGIN_' + import.meta.env.MODE,
          },
        });

        // set the usersnap api in the context
        api.on('open', (event) => {
          if (user.isLoggedIn) {
            fetchSystemSettings(undefined, true).then((response) => {
              const systemSettings = response.data?.settings;
              const apiVersion = systemSettings?.api_version || 'unknown';
              const configrepoVersion =
                systemSettings?.configrepo_version || 'unknown';
              const hostName = systemSettings?.hostname || 'unknown';

              event.api.setValue('custom', {
                apiVersion,
                configrepoVersion,
                hostName,
              });
            });
          }
        });
        setUsersnapApi(api);
      });
    }
  }, [usersnapGlobalApiKey, user.email]);

  return <UsersnapContext value={usersnapApi} />;
};
